import { shortDate } from "../lib/format";
import { T, useI18n } from "../lib/i18n";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Logo } from "../components/Logo";

type LegalKind = "terms" | "privacy" | "subscription-agreement";

const legalVersions: Record<
  LegalKind,
  { effectiveDate: Date; version: string }
> = {
  terms: { effectiveDate: new Date(2026, 6, 20), version: "2026-07-20" },
  privacy: { effectiveDate: new Date(2026, 6, 20), version: "2026-07-20" },
  "subscription-agreement": {
    effectiveDate: new Date(2026, 6, 20),
    version: "2026-07-20",
  },
};

const documents: Record<
  LegalKind,
  {
    title: string;
    summary: string;
    sections: Array<{ title: string; paragraphs: string[] }>;
  }
> = {
  terms: {
    title: "Terms of Service",
    summary:
      "These terms govern access to TidyPing’s cleaning-business scheduling, communication, team, invoice, and reporting services.",
    sections: [
      {
        title: "1. Agreement and eligibility",
        paragraphs: [
          "By creating an account, accepting an invitation, or using TidyPing, you agree to these Terms and our Privacy Policy. You must be legally able to enter this agreement and provide accurate account information.",
          "A person creating a business workspace represents that they are authorized to act for that cleaning business. Employees and clients may enter only through records and invitations authorized by the applicable business.",
        ],
      },
      {
        title: "2. Secure accounts",
        paragraphs: [
          "TidyPing uses Apple, Google, or Microsoft single sign-on. We do not receive your provider password. You are responsible for protecting your provider account, device access, and invitation links and for notifying us promptly about suspected unauthorized access.",
          "Employee invitations are single-use, time-limited, restricted to the invited verified email, and assigned a role by the business. Client invitations connect only the client record selected by the business.",
        ],
      },
      {
        title: "3. Service and business responsibilities",
        paragraphs: [
          "TidyPing provides software for scheduling, mutual confirmations, in-app chat, attachments, notifications, invoices, business records, and team administration. Cleaning services are provided by independent businesses, not by TidyPing.",
          "Businesses are responsible for service quality, worker authorization, taxes, insurance, prices, invoices, collection, safety, and compliance with employment, consumer, and privacy laws. Clients and businesses are responsible for the accuracy and lawfulness of content they upload.",
        ],
      },
      {
        title: "4. Acceptable use",
        paragraphs: [
          "You may not misuse the service, access another workspace without authorization, share private invitation codes improperly, upload unlawful or harmful content, probe security controls, automate abusive traffic, or interfere with another user. We may restrict access needed to protect users, the platform, or legal compliance.",
        ],
      },
      {
        title: "5. Messages, attachments, and notifications",
        paragraphs: [
          "In-app messages and attachments are visible only to authorized participants in the connected business-client relationship. Push delivery can be delayed or affected by device, network, or Apple services. Users should not rely on a notification as the sole channel for emergencies or time-critical safety matters.",
        ],
      },
      {
        title: "6. Financial records and invoices",
        paragraphs: [
          "TidyPing currently records cleaning prices, payment status, revenue, and invoices but does not process the client’s cleaning payment. A “paid” status is a business-entered record and is not proof that TidyPing transferred funds. Only business owners receive financial dashboards.",
        ],
      },
      {
        title: "7. Trial and business subscriptions",
        paragraphs: [
          "A new eligible business receives 30 days of complimentary access without payment information, charge, or automatic renewal. When that period ends, business operations pause until the owner activates an eligible plan.",
          "An owner may subscribe before complimentary access ends. iOS purchases are billed by Apple and website purchases are billed by Square. Paid service starts when the applicable checkout confirms the purchase; unused complimentary days are not added to the paid period. Clients do not purchase a TidyPing business plan.",
        ],
      },
      {
        title: "8. Content and intellectual property",
        paragraphs: [
          "You retain ownership of content you submit and grant TidyPing the limited right to host, process, transmit, and display it solely to operate, secure, support, and improve the service. TidyPing, its design, software, trademarks, and documentation remain owned by their respective rights holders.",
        ],
      },
      {
        title: "9. Suspension, deletion, and export",
        paragraphs: [
          "You may stop using the service or request account deletion from account settings. Owners should export business records before deleting a sole-owner workspace. Deleting an account does not cancel an Apple or Square subscription; it must be canceled with the billing provider first.",
          "We may suspend or terminate access for material breach, fraud, security risk, nonpayment, or legal requirement. Where practical, we will provide notice and a reasonable opportunity to export data.",
        ],
      },
      {
        title: "10. Disclaimers and limitation",
        paragraphs: [
          "The service is provided on an “as available” basis to the extent permitted by law. We do not guarantee uninterrupted delivery or the performance of any cleaning business or client. To the maximum extent permitted by law, indirect, special, incidental, or consequential damages are excluded. Rights that cannot legally be waived remain unaffected.",
        ],
      },
      {
        title: "11. Changes and contact",
        paragraphs: [
          "We may update these Terms with a revised effective date and appropriate notice for material changes. Continued use after the effective date means acceptance where permitted by law.",
          "Questions, notices, and support requests may be sent to support@maintechsolution.com.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    summary:
      "This policy explains what TidyPing collects, why it is used, who can access it, and the choices available to business owners, employees, and clients.",
    sections: [
      {
        title: "1. Information we collect",
        paragraphs: [
          "Account information includes provider identity, verified email, display name, optional phone, locale, and authentication metadata. Business information can include company identity, contact details, service addresses, team roles, client records, schedules, prices, invoice records, and business settings.",
          "Service content includes in-app messages, photos, videos, files, confirmation activity, support requests, device push tokens, presence status, security logs, and technical diagnostics. We do not collect or store your Apple, Google, or Microsoft password.",
        ],
      },
      {
        title: "2. No employee location tracking",
        paragraphs: [
          "TidyPing does not track, store, or expose an employee’s continuous or background location. A user may choose to open an address in an installed navigation app, but TidyPing does not use that action to monitor the employee.",
        ],
      },
      {
        title: "3. How information is used",
        paragraphs: [
          "We use information to authenticate users, connect authorized relationships, deliver schedules and real-time chat, send notifications, generate invoices and reports, enforce roles and subscription access, prevent abuse, answer support requests, maintain audit records, and comply with law.",
          "We do not sell personal information and do not use private client or employee data for third-party behavioral advertising.",
        ],
      },
      {
        title: "4. Role-based sharing",
        paragraphs: [
          "Business owners and authorized operational members can access workspace information needed for their roles. Only owners can access client prices, payments, invoices, revenue, and subscription administration. A linked client can access only that client’s connected schedules, messages, attachments, and invoices.",
          "Employee and client access requires an authorized invitation. Server-side row-level security and audited functions enforce these boundaries independently of the app interface.",
        ],
      },
      {
        title: "5. Service providers",
        paragraphs: [
          "We use service providers such as Supabase for authentication, database, storage, realtime features, and server functions; Apple for iOS distribution, notifications, and in-app subscription billing; Square for website subscription checkout and billing; and email delivery providers for support. These providers process data under their own terms and safeguards.",
          "Square receives payment and billing details entered in its hosted checkout. TidyPing stores provider customer, subscription, plan, status, and audit identifiers needed to grant access, but does not store full card numbers. OAuth providers process sign-in according to the provider you select.",
        ],
      },
      {
        title: "6. Retention and deletion",
        paragraphs: [
          "We retain account and workspace information while needed to provide the service and for legitimate security, support, tax, dispute, and legal obligations. Archived business records may remain available to the authorized workspace until deleted under the business retention process.",
          "You can request account deletion inside the app or website. Deletion may remove a sole-owner workspace and connected records after confirmation. Some minimal audit, transaction, or legal records may be retained where required or permitted.",
        ],
      },
      {
        title: "7. Security",
        paragraphs: [
          "We use encrypted network transport, provider authentication, private storage, row-level database access, role protections, hashed invitation codes, limited service credentials, security logging, and rate limits. No system can be guaranteed completely secure, and users must protect their devices and provider accounts.",
        ],
      },
      {
        title: "8. Your choices and rights",
        paragraphs: [
          "You can update profile information, control iOS notification permission, export invoices, request correction or deletion, and contact us about access or privacy rights. Depending on where you live, additional rights may apply, including objection, restriction, portability, or appeal.",
          "TidyPing is not directed to children under 13 and is intended for adults managing or receiving cleaning services.",
        ],
      },
      {
        title: "9. International processing and changes",
        paragraphs: [
          "Data may be processed in the United States or other locations where our providers operate, subject to applicable safeguards. We will post material policy changes with a new effective date and provide additional notice where required.",
        ],
      },
      {
        title: "10. Contact",
        paragraphs: [
          "For privacy questions or requests, contact support@maintechsolution.com with “Privacy Request” in the subject. We may need to verify your identity before completing a request.",
        ],
      },
    ],
  },
  "subscription-agreement": {
    title: "Business Subscription Agreement",
    summary:
      "This agreement applies when an authorized business owner activates a TidyPing plan through Apple in the iOS app or Square on the TidyPing website.",
    sections: [
      {
        title: "1. Owner authority and scope",
        paragraphs: [
          "By accepting this agreement and confirming a purchase, you represent that you are the business owner or have authority to purchase the plan for the workspace. One business subscription covers the authorized members and connected clients of that workspace within the selected plan limits.",
        ],
      },
      {
        title: "2. Complimentary access",
        paragraphs: [
          "An eligible new business receives 30 days of complimentary Pro-level access. No payment information is required, no charge occurs, and the complimentary period does not automatically convert or renew. The owner may subscribe at any time; if the owner subscribes early, paid billing starts immediately and unused complimentary days are not added to the paid period.",
        ],
      },
      {
        title: "3. Plans and capacity",
        paragraphs: [
          "Starter supports up to 2 active team members, including the owner, and 75 active clients. Growth supports up to 10 active team members, including the owner, and 300 active clients. Pro supports up to 30 active team members, including the owner, and unlimited active clients, subject to reasonable use and technical safeguards.",
          "All plans include scheduling and two-sided confirmations, authorized in-app client chat with photo and video attachments, invoices, owner-only financial reporting, and access on supported iPhone, iPad, and web sessions. The purchase screen describes current features and capacity before payment.",
        ],
      },
      {
        title: "4. Billing, renewal, and taxes",
        paragraphs: [
          "The price, currency, and billing period shown by Apple or Square at confirmation control the transaction. Payment is charged when the purchase is confirmed. Monthly and annual subscriptions renew automatically until canceled through the same billing provider. Applicable taxes, if any, are displayed or handled under that provider’s checkout and policies.",
          "Apple-billed subscriptions are managed in Apple Account subscription settings. Website plan changes and cancellation are managed in TidyPing, with payment processing provided securely by Square. Deleting TidyPing or a TidyPing account does not cancel either subscription. Refund requests and billing disputes are subject to the applicable provider policies and law.",
        ],
      },
      {
        title: "5. Activation and cross-device access",
        paragraphs: [
          "TidyPing verifies Apple transactions with the App Store Server API and Square transactions through signed webhooks and authenticated API reconciliation, then links the verified entitlement to one business workspace. Only one billing provider may be active for that workspace at a time. Active entitlement applies across supported iPhone, iPad, and web sessions and cannot be moved to an unrelated workspace without support review.",
        ],
      },
      {
        title: "6. Changes, expiration, and restoration",
        paragraphs: [
          "Plan changes, renewal dates, cancellation timing, and any proration are handled by the active billing provider. A change to a lower-capacity plan can require the owner to archive clients or deactivate team members first. If billing enters an eligible grace period, access may continue temporarily; when entitlement expires, is refunded, or is revoked, operational changes pause while records remain available for owner review and support.",
          "Use Restore Purchases in the iOS app when an eligible Apple purchase does not appear. For a website subscription, use TidyPing Plans & billing to change the plan, billing period, or cancellation status. Contact support for payment-method assistance.",
        ],
      },
      {
        title: "7. Business records and client payments",
        paragraphs: [
          "The subscription pays for TidyPing software. It does not pay for cleaning services and does not cause TidyPing to collect client invoice payments. The business remains responsible for pricing, collection, tax, payroll, insurance, and service obligations.",
        ],
      },
      {
        title: "8. Acceptance and contact",
        paragraphs: [
          "You must affirmatively accept this agreement before the Apple purchase sheet or Square-hosted checkout opens. TidyPing records the agreement version, workspace, account, time, and purchase channel for audit purposes.",
          "For subscription support, contact support@maintechsolution.com. Apple billing can be reviewed in Apple Account subscription settings; website plan changes and cancellation are available in TidyPing Plans & billing.",
        ],
      },
    ],
  },
};

export function LegalPage() {
  const { document = "" } = useParams();
  const { t } = useI18n();
  if (!(document in documents)) return <Navigate to="/legal/terms" replace />;
  const legal = documents[document as LegalKind];
  const legalVersion = legalVersions[document as LegalKind];

  return (
    <main className="legal-page">
      <header className="legal-header">
        <Logo />
        <nav>
          <Link to="/legal/terms">
            <T>{"Terms"}</T>
          </Link>
          <Link to="/legal/privacy">
            <T>{"Privacy"}</T>
          </Link>
          <Link to="/legal/subscription-agreement">
            <T>{"Subscription"}</T>
          </Link>
          <Link to="/support">
            <T>{"Support"}</T>
          </Link>
        </nav>
      </header>
      <article className="legal-document">
        <Link className="legal-back" to="/">
          <ArrowLeft size={16} />
          <T>{"Back to TidyPing"}</T>
        </Link>
        <span className="legal-document__icon">
          <ShieldCheck size={25} />
        </span>
        <span className="eyebrow">
          <T>{"TidyPing legal"}</T>
        </span>
        <h1>{t(legal.title)}</h1>
        <p className="legal-summary">{t(legal.summary)}</p>
        <p className="legal-effective">
          <T>{"Effective"}</T> {shortDate(legalVersion.effectiveDate)} ·{" "}
          {t("Version {version}", { version: legalVersion.version })}
        </p>
        <div className="legal-sections">
          {legal.sections.map((section) => (
            <section key={section.title}>
              <h2>{t(section.title)}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{t(paragraph)}</p>
              ))}
            </section>
          ))}
        </div>
      </article>
      <footer className="legal-footer">
        <T>{"TidyPing ·"}</T>{" "}
        <a href="mailto:support@maintechsolution.com">
          <T>{"support@maintechsolution.com"}</T>
        </a>
      </footer>
    </main>
  );
}
